import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Expense } from '@/types/expense';
import { format } from 'date-fns';

export const generateTripReport = (tripName: string, expenses: Expense[]) => {
  const doc = new jsPDF();
  
  // Add title and basic info
  doc.setFontSize(20);
  doc.text(`Trip Report: ${tripName}`, 14, 22);
  
  // Calculate summary statistics
  const totalAmount = expenses.reduce((sum, exp) => sum + exp.amount, 0);
  const startDate = expenses[0]?.date ? new Date(expenses[0].date) : new Date();
  const endDate = expenses[expenses.length - 1]?.date ? new Date(expenses[expenses.length - 1].date) : new Date();
  const tripDuration = Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const uniqueParticipants = new Set(expenses.flatMap(e => e.participants || []));
  
  // Add summary section
  doc.setFontSize(12);
  doc.text('Trip Summary', 14, 35);
  doc.setFontSize(10);
  doc.text(`Total Expenses: ₹${totalAmount.toFixed(2)}`, 14, 45);
  doc.text(`Trip Duration: ${tripDuration} days`, 14, 52);
  doc.text(`Number of Participants: ${uniqueParticipants.size}`, 14, 59);
  doc.text(`Average per Day: ₹${(totalAmount / tripDuration).toFixed(2)}`, 14, 66);
  
  // Category breakdown
  const categoryTotals = expenses.reduce((acc, exp) => {
    acc[exp.category] = (acc[exp.category] || 0) + exp.amount;
    return acc;
  }, {} as Record<string, number>);
  
  doc.setFontSize(12);
  doc.text('Category Breakdown', 14, 80);
  
  const categoryData = Object.entries(categoryTotals).map(([category, amount]) => [
    category,
    `₹${amount.toFixed(2)}`,
    `${((amount / totalAmount) * 100).toFixed(1)}%`
  ]);
  
  autoTable(doc, {
    head: [['Category', 'Amount', 'Percentage']],
    body: categoryData,
    startY: 85,
    styles: { fontSize: 10 },
    headStyles: { fillColor: [155, 135, 245] }
  });
  
  // Detailed expenses table
  doc.addPage();
  doc.setFontSize(12);
  doc.text('Detailed Expenses', 14, 20);
  
  const expenseData = expenses.map(expense => [
    format(new Date(expense.date), 'MMM dd, yyyy'),
    expense.description,
    expense.category,
    expense.paidBy || 'N/A',
    `₹${expense.amount.toFixed(2)}`
  ]);
  
  autoTable(doc, {
    head: [['Date', 'Description', 'Category', 'Paid By', 'Amount']],
    body: expenseData,
    startY: 25,
    styles: { fontSize: 10 },
    headStyles: { fillColor: [155, 135, 245] }
  });
  
  // Save the PDF
  doc.save(`${tripName.toLowerCase().replace(/\s+/g, '_')}_report.pdf`);
};